import React, { useEffect, useState } from 'react';
import { 
  Zap, 
  Shield,
  Clock,
  Award,
  Users,
  Sparkles,
  ChevronRight,
  Battery,
  Cpu,
  CloudLightning,
  CheckCircle
} from 'lucide-react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useSelector } from 'react-redux';
import SectionHeader from '../SectionHeader';

function WhyUsSection() {
  const contentData = useSelector(state => state.Data.contentData);
  const [activeIndex, setActiveIndex] = useState(0);
  
  const benefits = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: contentData?.why_1_title || "Premium Quality",
      description: contentData?.why_1_desc || "Certified electrical materials and professional installation",
      color: "blue",
      gradient: "from-blue-500 via-blue-600 to-blue-700",
      iconBg: "bg-gradient-to-br from-blue-500 to-blue-600",
      number: "01",
      features: ["Certified materials", "Professional installation", "Quality assurance"]
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: contentData?.why_2_title || "Quick Response",
      description: contentData?.why_2_desc || "Emergency service available 24/7 with fast response time",
      color: "amber",
      gradient: "from-amber-500 via-amber-600 to-amber-700",
      iconBg: "bg-gradient-to-br from-amber-500 to-amber-600",
      number: "02",
      features: ["24/7 emergency service", "Fast response time", "Quick solutions"]
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: contentData?.why_3_title || "Expert Team",
      description: contentData?.why_3_desc || "Licensed electricians with years of experience",
      color: "emerald",
      gradient: "from-emerald-500 via-emerald-600 to-emerald-700",
      iconBg: "bg-gradient-to-br from-emerald-500 to-emerald-600",
      number: "03",
      features: ["Licensed electricians", "Years of experience", "Professional training"]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: contentData?.why_4_title || "Customer Focus",
      description: contentData?.why_4_desc || "Personalized solutions and excellent after-service support",
      color: "purple",
      gradient: "from-purple-500 via-purple-600 to-purple-700",
      iconBg: "bg-gradient-to-br from-purple-500 to-purple-600",
      number: "04",
      features: ["Personalized solutions", "After-service support", "Customer satisfaction"]
    }
  ];

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic'
    });
  }, []);

  return (
    <section className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white">
      {/* Background Decoration */}
      <div className="absolute inset-0">
        {/* Circuit Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-10 w-32 h-px bg-gradient-to-r from-blue-500 to-transparent"></div>
          <div className="absolute bottom-1/3 right-10 w-24 h-px bg-gradient-to-l from-amber-500 to-transparent"></div>
          <div className="absolute top-1/2 left-1/4 w-16 h-px bg-gradient-to-r from-emerald-500 to-transparent"></div>
        </div>
        
        {/* Floating Dots */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <div 
              key={i}
              className="absolute w-1 h-1 bg-gradient-to-r from-blue-400 to-amber-400 rounded-full opacity-20"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
          <SectionHeader title={contentData?.section_5} />
        {/* Benefits Grid - Full Width */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20" data-aos="fade-up" data-aos-delay="200">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="group relative"
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => setActiveIndex(index)}
            >
              <div className={`
                relative bg-white rounded-2xl p-6 border border-gray-200
                transition-all duration-500 ease-out
                hover:border-${benefit.color}-200 hover:shadow-xl
                h-full flex flex-col
                ${activeIndex === index ? `border-${benefit.color}-300 shadow-lg` : ''}
              `}>
                {/* Top Section - Icon & Number */}
                <div className="flex items-start justify-between mb-6">
                  {/* Icon Container */}
                  <div className={`relative`}>
                    <div className={`w-14 h-14 ${benefit.iconBg} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <div className="text-white">
                        {benefit.icon}
                      </div>
                    </div>
                    {/* Number Badge */}
                    <div className={`absolute -top-3 -left-3 w-10 h-10 ${benefit.iconBg} rounded-xl flex items-center justify-center shadow-md`}>
                      <span className="text-white font-bold text-sm">{benefit.number}</span>
                    </div>
                  </div>
                  
                  {/* Arrow Indicator */}
                  <ChevronRight className={`
                    w-5 h-5 text-gray-400 mt-1
                    transition-all duration-300
                    ${activeIndex === index ? 'transform rotate-90 text-blue-500' : 'group-hover:translate-x-1'}
                  `} />
                </div>
                
                {/* Content Section */}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-800 transition-colors">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {benefit.description}
                  </p>
                  
                  {/* Features List - Expandable */}
                  <div className={`mt-4 overflow-hidden transition-all duration-500 max-h-48 opacity-100`}>
                    
                  </div>
                </div>
                
                {/* Progress Line */}
                <div className={`absolute bottom-0 left-0 h-1.5 bg-gradient-to-r ${benefit.gradient} rounded-b-2xl transition-all duration-500 w-full`}></div>
              </div>
              
              {/* Glow Effect */}
              <div className={`absolute inset-0 ${benefit.gradient} opacity-0 group-hover:opacity-5 blur-xl rounded-2xl -z-10 transition-opacity duration-500`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default WhyUsSection;